/**
 * Prompt Building Functions
 * Handles system prompts, user prompts, and difficulty instructions
 */

import { 
  getTemplateById, 
  getTemplateSystemPrompt,
  getTemplateDefaultSettings,
  PROMPT_TEMPLATES 
} from './promptTemplates';

// Re-export template functions for convenience
export { 
  getTemplateById, 
  getTemplateSystemPrompt,
  getTemplateDefaultSettings,
  getTemplatesByCategory,
  getCategoriesWithTemplates,
  getAllTemplateIds,
  getTemplateExampleTags,
  TEMPLATE_CATEGORIES,
  PROMPT_TEMPLATES
} from './promptTemplates';

/**
 * Build system prompt from a template ID
 * Falls back to default prompt if template not found
 */
export function buildSystemPromptFromTemplate(templateId, customOverrides = {}) {
  const prompt = getTemplateSystemPrompt(templateId, customOverrides);
  return prompt || getDefaultSystemPrompt();
}

/**
 * Get the default German system prompt with examples
 */
export function getDefaultSystemPrompt() {
  return `Du bist ein Experte für die Erstellung von Quiz-Fragen. Deine Aufgabe ist es, qualitativ hochwertige, lehrreiche Quiz-Fragen basierend auf den bereitgestellten Inhalten zu erstellen.

KRITISCH: Du musst NUR mit gültigem JSON antworten. Füge keine Markdown-Formatierung, Code-Blöcke oder erklärenden Text hinzu.

JSON Schema:
[
  {
    "question": "string - Der Text der Quiz-Frage",
    "codeBlock": {
      "language": "string - Programmiersprache (python, javascript, java, csharp, cpp, bash, sql, etc.)",
      "code": "string - Code-Snippet (kann ein leerer String sein, wenn kein Code vorhanden ist)"
    },
    "terminalOutput": "string - erwartete Terminal-Ausgabe (optional, kann ein leerer String sein)",
    "options": ["string array - 2-6 Antwortmöglichkeiten"],
    "correctAnswerIndices": "[number array] - Array mit Index/Indizes der korrekten Antwort(en). WICHTIG: Variiere die Positionen! z.B. [0], [1], [2], [3] oder [0,2] bei Multiple Choice",
    "explanation": "string - Erklärung der Lösung (optional)"
  }
]

WICHTIG für correctAnswerIndices:
- Bei Single Choice: Array mit einem Element, z.B. [0], [1], [2], oder [3]
- Bei Multiple Choice: Array mit mehreren Elementen, z.B. [0, 2] oder [1, 3]
- VERTEILE die korrekten Antworten gleichmäßig über alle Positionen!
- Setze NICHT immer [0] als korrekte Antwort!

BEISPIEL 1 - Python-Frage mit Code und Terminal-Output:
{
  "question": "Was gibt dieser Python-Code aus?",
  "codeBlock": {
    "language": "python",
    "code": "numbers = [1, 2, 3, 4, 5]\\nresult = sum(numbers)\\nprint(result)"
  },
  "terminalOutput": "15",
  "options": [
    "12345",
    "15",
    "[1, 2, 3, 4, 5]",
    "Error"
  ],
  "correctAnswerIndices": [1]
}

BEISPIEL 2 - Konzeptfrage OHNE Code:
{
  "question": "Was ist der Hauptvorteil von Dependency Injection?",
  "codeBlock": {
    "language": "",
    "code": ""
  },
  "terminalOutput": "",
  "options": [
    "Schnellere Ausführungsgeschwindigkeit",
    "Geringerer Speicherverbrauch",
    "Bessere Testbarkeit und lose Kopplung",
    "Einfacherer Code"
  ],
  "correctAnswerIndices": [2]
}

BEISPIEL 3 - JavaScript/React-Frage mit Code:
{
  "question": "Welcher React Hook wird verwendet, um Seiteneffekte in Funktionskomponenten auszuführen?",
  "codeBlock": {
    "language": "javascript",
    "code": "import React, { useState, useEffect } from 'react';\\n\\nfunction DataFetcher() {\\n  const [data, setData] = useState(null);\\n  \\n  // Welcher Hook kommt hier hin?\\n  _____(( ) => {\\n    fetchData().then(setData);\\n  }, []);\\n}"
  },
  "terminalOutput": "",
  "options": [
    "useState",
    "useCallback",
    "useMemo",
    "useEffect"
  ],
  "correctAnswerIndices": [3]
}

BEISPIEL 4 - Multiple-Choice-Frage (mehrere richtige Antworten):
{
  "question": "Welche Aussagen über Python-Listen sind korrekt? (Mehrere Antworten möglich)",
  "codeBlock": {
    "language": "",
    "code": ""
  },
  "terminalOutput": "",
  "options": [
    "Listen sind veränderbar (mutable)",
    "Listen können verschiedene Datentypen enthalten",
    "Listen sind immer sortiert",
    "Listen haben eine feste Größe"
  ],
  "correctAnswerIndices": [0, 1]
}

BEISPIEL 5 - SQL-Frage mit Code:
{
  "question": "Was macht diese SQL-Abfrage?",
  "codeBlock": {
    "language": "sql",
    "code": "SELECT name, COUNT(*) as total\\nFROM orders\\nGROUP BY name\\nHAVING COUNT(*) > 5;"
  },
  "terminalOutput": "",
  "options": [
    "Zählt alle Bestellungen",
    "Löscht Bestellungen mit mehr als 5 Einträgen",
    "Zeigt die ersten 5 Bestellungen pro Name",
    "Zeigt Namen mit mehr als 5 Bestellungen"
  ],
  "correctAnswerIndices": [3]
}

Richtlinien:
- Erstelle Fragen, die Verständnis prüfen, nicht nur Auswendiglernen
- Variiere zwischen Fragen MIT und OHNE Code
- Füge Code-Beispiele hinzu, wenn relevant
- Bei Single Choice: correctAnswerIndices enthält genau ein Element
- Bei Multiple Choice: correctAnswerIndices enthält mehrere Elemente
- Mache falsche Antworten (Distraktoren) plausibel aber eindeutig falsch
- Variiere Schwierigkeitsgrad und Fragentypen
- Halte Fragen klar und prägnant
- Verwende korrekte Formatierung für Code-Blöcke
- Antworte NUR mit dem JSON-Array, ohne Markdown oder zusätzlichen Text`;
}

/**
 * Get difficulty-specific instructions
 */
export function getDifficultyInstructions(difficulty) {
  const instructions = {
    easy: `
SCHWIERIGKEIT: LEICHT
- Fokussiere auf grundlegende Konzepte und Syntax
- Verwende einfache, direkte Code-Beispiele
- Stelle sicher, dass Anfänger die Fragen beantworten können
- Vermeide verschachtelte oder komplexe Logik`,
    
    medium: `
SCHWIERIGKEIT: MITTEL
- Teste Verständnis von Konzepten und deren Anwendung
- Verwende realistische Code-Beispiele
- Kombiniere mehrere Konzepte in einer Frage
- Erfordere logisches Denken, nicht nur Auswendiglernen`,
    
    hard: `
SCHWIERIGKEIT: SCHWER
- Teste tiefes Verständnis und fortgeschrittene Konzepte
- Verwende komplexe Code-Beispiele mit mehreren Ebenen
- Erfordere Analyse und kritisches Denken
- Berücksichtige Edge Cases und ungewöhnliche Szenarien
- Kombiniere mehrere fortgeschrittene Konzepte`
  };
  
  return instructions[difficulty] || instructions.medium;
}

/**
 * Build user prompt for question generation
 */
export function buildUserPrompt(options) {
  const {
    numberOfQuestions,
    difficulty,
    includeCodeBlocks,
    multipleChoiceMode = 'single', // 'single', 'multiple', 'mixed'
    topicTags,
    userGuidance,
    fileContents,
    existingQuestions = [] // Existing questions to avoid duplicates
  } = options;

  // Multiple Choice instructions based on mode
  let mcInstructions = '';
  if (multipleChoiceMode === 'single') {
    mcInstructions = `\nANTWORTTYP: Erstelle nur Single-Choice-Fragen (genau EINE richtige Antwort).
Bei Single Choice: correctAnswerIndices enthält genau ein Element, z.B. [0], [1], [2], oder [3].`;
  } else if (multipleChoiceMode === 'multiple') {
    mcInstructions = `\nANTWORTTYP: Erstelle nur Multiple-Choice-Fragen (MEHRERE richtige Antworten).
Bei Multiple Choice: correctAnswerIndices enthält MEHRERE Elemente, z.B. [0, 2] oder [1, 3].
Füge bei jeder Frage einen Hinweis hinzu wie "(Mehrere Antworten möglich)" oder "(Wählen Sie alle zutreffenden)".`;
  } else {
    mcInstructions = `\nANTWORTTYP: Erstelle einen Mix aus Single-Choice und Multiple-Choice-Fragen.
- Bei Single Choice: correctAnswerIndices enthält genau ein Element, z.B. [0]
- Bei Multiple Choice: correctAnswerIndices enthält mehrere Elemente, z.B. [0, 2]
- Füge bei Multiple Choice einen Hinweis hinzu wie "(Mehrere Antworten möglich)"`;
  }

  let prompt = `Erstelle ${numberOfQuestions} Quiz-Fragen.

${getDifficultyInstructions(difficulty)}
${mcInstructions}

${includeCodeBlocks ? 'CODE: Füge Code-Beispiele hinzu, wo sinnvoll.' : 'CODE: Erstelle konzeptionelle Fragen OHNE Code-Blöcke.'}
`;

  // Add existing questions context if appending
  if (existingQuestions && existingQuestions.length > 0) {
    prompt += `\nWICHTIG - BESTEHENDE FRAGEN VERMEIDEN:
Es gibt bereits ${existingQuestions.length} Fragen. Erstelle NEUE, UNTERSCHIEDLICHE Fragen!
Vermeide Duplikate oder zu ähnliche Fragen zu diesen bestehenden Themen:
${existingQuestions.slice(0, 10).map((q, i) => `${i + 1}. "${q.question.substring(0, 100)}..."`).join('\n')}
${existingQuestions.length > 10 ? `... und ${existingQuestions.length - 10} weitere` : ''}
`;
  }

  if (topicTags && topicTags.length > 0) {
    prompt += `\nTHEMEN/TAGS: Fokussiere auf folgende Themen:\n${topicTags.map(tag => `- ${tag}`).join('\n')}\n`;
  }

  if (userGuidance && userGuidance.trim()) {
    prompt += `\nZUSÄTZLICHE ANWEISUNGEN:\n${userGuidance}\n`;
  }

  if (fileContents && fileContents.length > 0) {
    prompt += `\nBASIS-INHALTE:\n`;
    fileContents.forEach((file, index) => {
      prompt += `\n--- Datei ${index + 1}: ${file.name} ---\n${file.content}\n`;
    });
  }

  prompt += `\nAntworte NUR mit einem JSON-Array von Fragen. Keine Erklärungen, kein Markdown.`;

  return prompt;
}

/**
 * Build prompt for question variation
 * Creates a similar question respecting the includeCodeBlocks setting
 * @param {Object} originalQuestion - The question to create a variation of
 * @param {Object} options - Options
 * @param {boolean} options.includeCodeBlocks - Whether code blocks should be included (from settings)
 * @param {Array} options.allQuestions - All generated questions for context
 * @param {number} options.questionIndex - Index of the question being varied
 */
export function buildVariationPrompt(originalQuestion, options = {}) {
  // Priority: use includeCodeBlocks setting from generation config
  // This respects the "Mit Code"/"Ohne Code" button setting
  const includeCode = options.includeCodeBlocks;
  const allQuestions = options.allQuestions || [];
  const questionIndex = options.questionIndex;
  
  let instruction = `Erstelle eine VARIATION der folgenden Frage. Behalte das Thema und den Schwierigkeitsgrad bei, aber ändere die Formulierung und die Antwortoptionen.`;
  
  if (includeCode === false) {
    // User explicitly set "ohne Code" - respect this!
    instruction += `\nWICHTIG: Erstelle eine reine Textfrage OHNE Code. Setze codeBlock.code und terminalOutput auf leere Strings ("").`;
  } else if (includeCode === true) {
    instruction += `\nFüge ein passendes Code-Beispiel hinzu, wenn sinnvoll.`;
  }
  // If includeCode is undefined, let the LLM decide based on original question
  
  // Add context of other questions if provided
  let contextSection = '';
  if (allQuestions.length > 1) {
    contextSection = `\n\nKONTEXT - Bisherige generierte Fragen (vermeide zu ähnliche Fragen):
${allQuestions.map((q, i) => `${i + 1}. "${q.question.substring(0, 80)}..."`).join('\n')}

Du erstellst eine Variation von Frage ${questionIndex + 1}. Die neue Frage wird hinzugefügt.\n`;
  }
  
  return `${instruction}
${contextSection}
Original-Frage:
${JSON.stringify(originalQuestion, null, 2)}

Erstelle EINE ähnliche aber unterschiedliche Frage zum selben Thema. ${includeCode === false ? 'Setze codeBlock.code und terminalOutput auf leere Strings ("").' : ''} Antworte nur mit einem JSON-Objekt (nicht als Array).`;
}

/**
 * Build prompt for rephrasing a question
 * Keeps the same concept and answers, but rephrases the question text
 * @param {Object} originalQuestion - The question to rephrase
 * @param {Object} options - Options
 * @param {boolean} options.includeCodeBlocks - Whether code blocks should be included
 */
export function buildRephrasingPrompt(originalQuestion, options = {}) {
  const includeCode = options.includeCodeBlocks;
  const hasCode = originalQuestion.codeBlock?.code && originalQuestion.codeBlock.code.trim().length > 0;
  const allQuestions = options.allQuestions || [];
  const questionIndex = options.questionIndex;
  
  let instruction = `FORMULIERE die folgende Frage UM. Behalte das GLEICHE Konzept und die GLEICHE Schwierigkeit bei, aber ändere die Formulierung des Fragetexts.`;
  
  if (includeCode === false) {
    instruction += `\nWICHTIG: Erstelle eine reine Textfrage OHNE Code.`;
  } else if (hasCode) {
    instruction += `\nDer Code-Block soll erhalten bleiben oder leicht angepasst werden.`;
  }
  
  instruction += `\n\nWICHTIG: Die Antwortoptionen können leicht umformuliert werden, aber die KORREKTE ANTWORT muss inhaltlich gleich bleiben!`;
  
  // Add context of other questions if provided
  let contextSection = '';
  if (allQuestions.length > 1) {
    contextSection = `\n\nKONTEXT - Bisherige generierte Fragen (für Konsistenz):
${allQuestions.map((q, i) => `${i + 1}. "${q.question.substring(0, 80)}..."`).join('\n')}

Du formulierst Frage ${questionIndex + 1} um.\n`;
  }
  
  return `${instruction}
${contextSection}
Original-Frage:
${JSON.stringify(originalQuestion, null, 2)}

Formuliere diese Frage neu, behalte aber das gleiche Konzept und die korrekte Antwort bei. ${includeCode === false ? 'Setze codeBlock.code und terminalOutput auf leere Strings ("").' : ''} Antworte nur mit einem JSON-Objekt (nicht als Array).`;
}

/**
 * Build prompt for question reroll
 * Creates a completely new question about the same concept, respecting includeCodeBlocks setting
 * @param {Object} originalQuestion - The question to reroll
 * @param {Object} options - Options
 * @param {boolean} options.includeCodeBlocks - Whether code blocks should be included (from settings)
 * @param {Array} options.allQuestions - All generated questions for context
 * @param {number} options.questionIndex - Index of the question being rerolled
 */
export function buildRerollPrompt(originalQuestion, options = {}) {
  // Priority: use includeCodeBlocks setting from generation config
  // This respects the "Mit Code"/"Ohne Code" button setting
  const includeCode = options.includeCodeBlocks;
  const allQuestions = options.allQuestions || [];
  const questionIndex = options.questionIndex;
  
  let instruction = `Erstelle eine KOMPLETT NEUE Frage zum selben inhaltlichen Thema.`;
  
  if (includeCode === false) {
    // User explicitly set "ohne Code" - respect this!
    instruction += `\nWICHTIG: Erstelle eine reine Textfrage OHNE Code. Die Einstellung "Ohne Code" ist aktiv.`;
    instruction += `\nSetze codeBlock.language, codeBlock.code und terminalOutput auf leere Strings ("").`;
  } else if (includeCode === true) {
    instruction += `\nFüge ein passendes Code-Beispiel hinzu, wenn sinnvoll.`;
  }
  // If includeCode is undefined, let the LLM decide based on original question
  
  // Add context of other questions if provided
  let contextSection = '';
  if (allQuestions.length > 1) {
    contextSection = `\n\nKONTEXT - Bisherige generierte Fragen (vermeide Duplikate):
${allQuestions.map((q, i) => `${i + 1}. "${q.question.substring(0, 80)}..."`).join('\n')}

Du ersetzt Frage ${questionIndex + 1} durch eine völlig neue Frage zum selben Thema.\n`;
  }
  
  return `${instruction}
${contextSection}
Thema/Konzept der Originalfrage: "${originalQuestion.question}"

Erstelle eine völlig andere Frage, die das gleiche inhaltliche Konzept testet, aber mit anderem Ansatz und anderer Formulierung.

${includeCode === false ? 'WICHTIG: KEINE Code-Blöcke verwenden! Setze codeBlock.language, codeBlock.code und terminalOutput auf leere Strings ("").' : ''}

Antworte nur mit einem JSON-Objekt (nicht als Array).`;
}

/**
 * Build prompt for question refinement chat
 * @param {Object} params - Parameters
 * @param {Object} params.question - The question to refine
 * @param {string} params.userRequest - User's refinement request
 * @param {Array} params.conversationHistory - Previous messages in the conversation
 * @returns {Object} System and user prompts for refinement
 */
export function buildRefinementPrompt({ question, userRequest, conversationHistory = [] }) {
  const systemPrompt = `Du bist ein Experte für Quiz-Fragen-Optimierung. Der Benutzer möchte eine bestehende Frage verbessern oder anpassen.

AKTUELLE FRAGE:
${JSON.stringify(question, null, 2)}

DEINE AUFGABE:
1. Verstehe was der Benutzer ändern möchte
2. Erkläre kurz was du änderst
3. Liefere die verbesserte Frage als JSON

ANTWORT-FORMAT:
Beginne mit einer kurzen Erklärung (1-2 Sätze), dann gib die aktualisierte Frage als JSON-Objekt aus.
Trenne Erklärung und JSON mit "---JSON---".

Beispiel:
Ich habe die Frage schwieriger gemacht und ein komplexeres Code-Beispiel hinzugefügt.

---JSON---
{
  "question": "...",
  "options": [...],
  ...
}`;

  // Build conversation context
  let conversationContext = '';
  if (conversationHistory.length > 0) {
    conversationContext = '\n\nBISHERIGE KONVERSATION:\n';
    conversationHistory
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .slice(-6) // Keep last 6 messages for context
      .forEach(m => {
        conversationContext += `${m.role === 'user' ? 'Benutzer' : 'Assistent'}: ${m.content}\n`;
      });
  }

  const userPrompt = `${conversationContext}

BENUTZER-ANFRAGE: ${userRequest}

Beantworte die Anfrage und gib die aktualisierte Frage als JSON zurück.`;

  return { systemPrompt, userPrompt };
}

/**
 * Parse refinement response to extract message and updated question
 * @param {string} response - LLM response
 * @returns {Object} { message: string, updatedQuestion: Object|null }
 */
export function parseRefinementResponse(response) {
  if (!response) {
    return { message: 'Keine Antwort erhalten.', updatedQuestion: null };
  }

  // Check for JSON separator
  const jsonSeparator = '---JSON---';
  const separatorIndex = response.indexOf(jsonSeparator);
  
  if (separatorIndex !== -1) {
    const message = response.substring(0, separatorIndex).trim();
    const jsonPart = response.substring(separatorIndex + jsonSeparator.length).trim();
    
    try {
      const updatedQuestion = JSON.parse(jsonPart);
      return { message, updatedQuestion };
    } catch (e) {
      // Try to extract JSON from the response
      const jsonMatch = jsonPart.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          const updatedQuestion = JSON.parse(jsonMatch[0]);
          return { message, updatedQuestion };
        } catch (e2) {
          return { message: response, updatedQuestion: null };
        }
      }
      return { message, updatedQuestion: null };
    }
  }

  // No separator, try to find JSON in response
  const jsonMatch = response.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    try {
      const updatedQuestion = JSON.parse(jsonMatch[0]);
      const message = response.replace(jsonMatch[0], '').trim() || 'Frage wurde aktualisiert.';
      return { message, updatedQuestion };
    } catch (e) {
      return { message: response, updatedQuestion: null };
    }
  }

  return { message: response, updatedQuestion: null };
}
